<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Agentes de IA</h1>
        <p class="subtitle">Selecciona un agente para comenzar una conversación</p>
      </div>
      <!-- Solo admins ven el botón de gestión -->
      <Button
        v-if="authStore.isAdmin"
        label="Gestionar agentes"
        icon="pi pi-cog"
        severity="secondary"
        outlined
        @click="router.push('/admin/agents')"
      />
    </div>

    <!-- Filtro rápido por categoría -->
    <div class="category-tabs" v-if="agents.length > 0">
      <button
        v-for="cat in availableCategories"
        :key="cat.value"
        :class="['cat-tab', { active: activeCategory === cat.value }]"
        @click="activeCategory = cat.value"
      >
        <i :class="['pi', cat.icon]" />
        {{ cat.label }}
        <span class="cat-count">{{ countByCategory(cat.value) }}</span>
      </button>
    </div>

    <!-- Lista de agentes -->
    <div v-if="loading" class="loading-msg">
      <i class="pi pi-spin pi-spinner" /> Cargando agentes...
    </div>
    <div v-else-if="filteredAgents.length === 0 && !loading" class="empty-msg">
      <i class="pi pi-android" style="font-size:3rem;color:#d1d5db" />
      <p>No hay agentes disponibles en esta categoría.</p>
    </div>
    <div v-else class="agents-grid">
      <AgentCard
        v-for="agent in filteredAgents"
        :key="agent.id"
        :agent="agent"
        @chat="goToChat"
      />
    </div>
  </DefaultLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import AgentCard from '../components/agents/AgentCard.vue';
import Button from 'primevue/button';
import { agentsService } from '../services/agents';

const router = useRouter();
const authStore = useAuthStore();
const agents = ref([]);
const loading = ref(false);
const activeCategory = ref('todos');

const categoryDefs = [
  { value: 'todos',      label: 'Todos',      icon: 'pi-th-large' },
  { value: 'general',    label: 'General',    icon: 'pi-android' },
  { value: 'financiero', label: 'Financiero', icon: 'pi-chart-line' },
  { value: 'soporte',    label: 'Soporte',    icon: 'pi-headphones' },
  { value: 'ventas',     label: 'Ventas',     icon: 'pi-shopping-cart' },
  { value: 'legal',      label: 'Legal',      icon: 'pi-book' },
  { value: 'rrhh',       label: 'RRHH',       icon: 'pi-users' },
];

const availableCategories = computed(() => {
  const usedCats = new Set(agents.value.map(a => a.category));
  return categoryDefs.filter(c => c.value === 'todos' || usedCats.has(c.value));
});

const filteredAgents = computed(() => {
  if (activeCategory.value === 'todos') return agents.value;
  return agents.value.filter(a => a.category === activeCategory.value);
});

const countByCategory = (cat) => {
  if (cat === 'todos') return agents.value.length;
  return agents.value.filter(a => a.category === cat).length;
};

const goToChat = (agentId) => router.push(`/chat/${agentId}`);

onMounted(async () => {
  loading.value = true;
  try {
    agents.value = await agentsService.getAgents();
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.page-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 1.25rem; flex-wrap: wrap; gap: 1rem;
}
.page-header h1 { margin: 0; color: #1e293b; }
.subtitle { color: #6b7280; margin: 0.25rem 0 0; font-size: 0.875rem; }

.category-tabs {
  display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.5rem;
}
.cat-tab {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.4rem 0.875rem; border-radius: 20px; border: 1px solid #e5e7eb;
  background: white; cursor: pointer; font-size: 0.85rem; color: #6b7280;
  transition: all 0.15s;
}
.cat-tab:hover { border-color: #6366f1; color: #6366f1; }
.cat-tab.active { background: #6366f1; color: white; border-color: #6366f1; }
.cat-count {
  background: rgba(255,255,255,0.25); border-radius: 10px;
  padding: 0 0.4rem; font-size: 0.75rem; font-weight: 700;
}
.cat-tab:not(.active) .cat-count { background: #f3f4f6; color: #6b7280; }

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}
.loading-msg, .empty-msg {
  text-align: center; padding: 4rem; color: #9ca3af;
  display: flex; flex-direction: column; align-items: center; gap: 0.75rem;
}
</style>
